import { Combobox, Pill, PillsInput, useCombobox, Input } from "@mantine/core";
import { useMemo, useState } from "react";
import { useId } from "@mantine/hooks";
import clsx from "clsx";
import "@scss/base-components/variants.scss";
import dropdownStyles from "@scss/custom-components/form-fields-dropdowns.module.scss";
import counterStyles from "@scss/custom-components/custom-counter-multi-select.module.scss";

type CounterMSItem =
  | string
  | {
      value: string;
      label?: string;
      disabled?: boolean;
    };

type CounterMSClassNames = Partial<{
  // PillsInput slots
  root: string;
  input: string;
  error: string;
  label: string;
  required: string;
  section: string;
  wrapper: string;
  // Combobox slots
  dropdown: string;
  options: string;
  option: string;
  search: string;
  // Input.Wrapper slots
  description: string;
}>;

type CounterMSStyles = Partial<
  Record<keyof CounterMSClassNames, React.CSSProperties>
>;

type Props = {
  data: CounterMSItem[];

  value?: string[];
  defaultValue?: string[];
  onChange?: (val: string[]) => void;

  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;

  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean; // kept for parity; we don’t show a global clear icon
  variant?: "default" | "dark" | "darker" | "sky";
  nothingFoundMessage?: string;

  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;

  classNames?: CounterMSClassNames;
  styles?: CounterMSStyles;

  comboboxProps?: { withinPortal?: boolean };
  disabled?: boolean;
  readOnly?: boolean;

  width?: number;
  style?: React.CSSProperties;

  /** Collapse into "X selected" after this many selections (default 2) */
  summaryAfter?: number;
};

const normalise = (items: CounterMSItem[]) =>
  items.map((i) =>
    typeof i === "string"
      ? { value: i, label: i, disabled: false }
      : { value: i.value, label: i.label ?? i.value, disabled: !!i.disabled },
  );

export function CustomCounterMultiSelect({
  data,
  value,
  defaultValue = [],
  onChange,

  label,
  description,
  error,
  required,

  placeholder = "Select values",
  searchable = true,
  variant = "default",
  nothingFoundMessage = "Nothing found",

  leftSection,
  rightSection,

  classNames,
  styles,
  comboboxProps,
  disabled,
  readOnly,

  width,
  style,

  summaryAfter = 1,
}: Props) {
  const id = useId();
  const items = useMemo(() => normalise(data), [data]);

  // controlled / uncontrolled
  const [inner, setInner] = useState<string[]>(defaultValue);
  const selected = value ?? inner;
  const setSelected = (next: string[]) => {
    onChange?.(next);
    if (value === undefined) setInner(next);
  };

  const [search, setSearch] = useState("");

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const filtered = useMemo(
    () =>
      items.filter((i) => i.label.toLowerCase().includes(search.toLowerCase())),
    [items, search],
  );

  const toggle = (val: string) =>
    setSelected(
      selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val],
    );

  const handleClearAll = () => {
    setSelected([]);
    setSearch("");
  };

  const chevron = rightSection ?? <></>;

  const showSummary = selected.length > summaryAfter;

  return (
    <Input.Wrapper
      id={id}
      label={label}
      description={description}
      error={error}
      required={required}
      className="multi-select"
      data-variant={variant}
      data-error={!!error}
      classNames={{
        label: "label",
        required: "required",
        error: "error",
        description: "label",
        ...(classNames ?? {}),
      }}
      styles={{
        error: { marginTop: 5 },
        ...styles,
      }}
      style={{ width, ...style }}
    >
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => {
          if (disabled || readOnly) return;
          toggle(val);
          setSearch("");
        }}
        classNames={{
          dropdown: clsx(
            dropdownStyles.dropdown,
            dropdownStyles[`dropdown--${variant}`],
          ),
          options: dropdownStyles.options,
          option: dropdownStyles.option,
          search: dropdownStyles.search,
          ...(classNames ?? {}),
        }}
        {...(comboboxProps ?? {})}
      >
        <Combobox.Target>
          <PillsInput
            variant={variant as any}
            disabled={disabled}
            onMouseDown={(e) => {
              if (disabled) return;
              e.preventDefault();
              combobox.openDropdown();
            }}
            classNames={{
              root: "pills-input",
              input: "input",
              error: "error",
              label: "label",
              required: "required",
              section: "section",
              wrapper: "wrapper",
              ...(classNames ?? {}),
            }}
            styles={styles}
            leftSection={leftSection}
            rightSection={
              <span style={{ pointerEvents: "none" }}>{chevron}</span>
            }
            rightSectionPointerEvents="none"
          >
            {/* Pills then caret field */}
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                overflowX: "visible",
                gap: 6,
              }}
            >
              {!showSummary ? (
                selected.map((v) => (
                  <Pill
                    key={v}
                    withRemoveButton={!readOnly}
                    onRemove={() => toggle(v)}
                    className="pill"
                  >
                    {items.find((i) => i.value === v)?.label ?? v}
                  </Pill>
                ))
              ) : (
                <Pill
                  withRemoveButton={!readOnly}
                  onRemove={handleClearAll}
                  className="pill"
                >
                  {selected.length} selected
                </Pill>
              )}

              <PillsInput.Field
                id={id}
                placeholder={selected.length === 0 ? placeholder : ""}
                value={search}
                readOnly={!searchable || readOnly}
                aria-required={required || undefined}
                aria-invalid={!!error || undefined}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  combobox.openDropdown();
                  combobox.updateSelectedOptionIndex();
                }}
                onFocus={() => combobox.openDropdown()}
                style={{ minWidth: 1 }}
                aria-label={placeholder}
              />
            </div>
          </PillsInput>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {filtered.length === 0 ? (
              <Combobox.Empty>
                {nothingFoundMessage ?? "Nothing found"}
              </Combobox.Empty>
            ) : (
              filtered.map((item) => {
                const isSelected = selected.includes(item.value);
                return (
                  <Combobox.Option
                    key={item.value}
                    value={item.value}
                    data-checked={isSelected}
                    aria-selected={isSelected}
                    aria-disabled={item.disabled || undefined}
                    disabled={item.disabled}
                    className={counterStyles.option}
                  >
                    <span className={counterStyles.optionContent}>
                      {isSelected && (
                        <></>
                      )}
                      {item.label}
                    </span>
                    {isSelected && (
                      <span
                        className={counterStyles.removeIconWrapper}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(item.value);
                        }}
                      >
                         <></>
                      </span>
                    )}
                  </Combobox.Option>
                );
              })
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Input.Wrapper>
  );
}
